import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CustomerAddress from './checkout/CustomerAddress';
import DeliveryDetails from './checkout/DeliveryDetails';
import PaymentMethod from './checkout/PaymentMethod.jsx';
import {
  Box,
  Button,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { APP_URLS } from '../../urls.js';

function Checkout () {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const isAuthenticated = user?.is_authenticated || false;

  // Add initialization state
  const [initialized, setInitialized] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());

  const steps = ['Customer Address', 'Delivery Details', 'Payment Method'];

  const isStepSkipped = (step) => skipped.has(step);

  // Initialize steps and handle URL params
  useEffect(() => {
    if (!initialized) {
      const urlParams = new URLSearchParams(location.search);
      const stepParam = urlParams.get('step');
      const storedStep = localStorage.getItem('checkoutTargetStep');

      if (stepParam && !isNaN(parseInt(stepParam))) {
        setActiveStep(parseInt(stepParam));
        // Clean URL without triggering navigation
        window.history.replaceState({}, '', window.location.pathname);
      } else if (storedStep && !isNaN(parseInt(storedStep))) {
        setActiveStep(parseInt(storedStep));
      }

      setInitialized(true);
      localStorage.removeItem('checkoutTargetStep');
    }
  }, [location.search, initialized]);

  const handleNext = () => {
    console.log('Next button clicked - Current active step:', activeStep);

    if (activeStep === 1 && !isAuthenticated) {
      console.log('User not authenticated at step 1 - redirecting to login');
      localStorage.setItem('checkoutInProgress', 'true');
      localStorage.setItem('checkoutTargetStep', '2'); // Set to step 1 (first step) after login
      navigate('/app/patient_login');
      return;
    }

    const nextStep = activeStep + 1;
    console.log('Advancing to next step:', nextStep);
    setActiveStep(nextStep);
  };

  const handleBack = () =>
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  const handleReset = () => setActiveStep(0);
  const handleNavigation = (url) => navigate(url);

  return (
    <Box
      sx={{
			  m: {
			    xs: '3rem 1rem',
			    sm: '3rem 2rem',
			    md: '3rem',
			    lg: '3rem 10rem',
			    xl: '3rem 20rem'
        },
			  height: '100%',
			  backgroundColor: '#121212'
      }}
    >
      <Box padding='30px' overflow='auto' height='100%' mt='3rem'>
        <Box
          sx={{
					  display: 'flex',
					  justifyContent: 'space-between',
					  alignItems: 'center',
					  width: '100%'
          }}
        >
          <Typography variant='h4' sx={{ color: 'white' }}>
            Checkout
          </Typography>
          <Button
            onClick={() => handleNavigation(APP_URLS.shoppingCart)}
            sx={{
						  color: 'white',
						  borderColor: 'white',
						  border: '1px solid',
						  '&:hover': { borderColor: 'white' }
            }}
          >
            Go Back to shopping cart
          </Button>
        </Box>

        {/* Debug info - remove in production */}
        <Typography
          variant='caption'
          sx={{ color: 'gray', display: 'block', mt: 1 }}
        >
          Step: {activeStep} | Auth: {isAuthenticated ? 'Yes' : 'No'}
        </Typography>

        <Stepper
          activeStep={activeStep}
          sx={{ marginTop: '3rem', color: 'white' }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ color: 'white' }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length
          ? (
            <>
              <Typography sx={{ mt: 2, mb: 1, color: 'white' }}>
                All steps completed - you're finished
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button onClick={handleReset} sx={{ color: 'white' }}>
                  Reset
                </Button>
              </Box>
            </>
            )
          : (
            <>
              <Typography sx={{ mt: 2, mb: 1, color: 'white' }}>
                Step {activeStep + 1}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Paper
                  variant='elevation'
                  sx={{
								  padding: '30px',
								  borderRadius: '10px',
								  overflow: 'auto',
								  height: '100%',
								  marginTop: '3rem',
								  backgroundColor: '#1e1e1e',
								  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {activeStep === 0 && <CustomerAddress onNext={handleNext} />}
                  {activeStep === 1 && <DeliveryDetails onNext={handleNext} />}
                  {activeStep === 2 && <PaymentMethod onNext={handleNext} />}
                </Paper>
              </Box>
              <Box
                sx={{
							  display: 'flex',
							  flexDirection: 'row',
							  pt: 2,
							  justifyContent: 'space-between'
                }}
              >
                <Button
                  color='inherit'
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1, color: 'white' }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  sx={{ color: 'white' }}
                  disabled={activeStep === 2 && !isAuthenticated}
                >
                  {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                </Button>
              </Box>
            </>
            )}
      </Box>
    </Box>
  );
}

export default Checkout;
